import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, CalendarDays, Users, UserCheck, Shield,
  CircleDollarSign, Mail, Smartphone, CheckCircle2,
  ChevronRight, ChevronLeft, Sparkles, TrendingUp,
  MessageSquare, Star, Clock, Check, Bell, AlertTriangle,
  Home, BarChart2, Zap,
} from "lucide-react";
import { Toggle } from "../components/Toggle";

// ── Step definitions ──────────────────────────────────────────────────────────
const STEPS = [
  {
    id: "welcome", nav: "Benvenuto",
    title: "Benvenuto su Hedita", emoji: "👋",
    subtitle: "La piattaforma professionale per gestire strutture ricettive, prenotazioni e ospiti — tutto in un unico posto, senza limiti di strutture.",
    Icon: Sparkles, color: "#c87045", bg: "#fdeedd",
    features: ["Gestione multi-struttura senza limiti", "Dashboard KPI in tempo reale", "Integrazione iCal con tutti i canali"],
  },
  {
    id: "strutture", nav: "Strutture",
    title: "Le tue strutture", emoji: "🏡",
    subtitle: "Aggiungi ville, appartamenti, B&B, masserie e trulli. Gestisci camere, tariffe, foto e regolamento per ogni proprietà.",
    Icon: Building2, color: "#c87045", bg: "#fdeedd",
    features: ["Strutture e camere illimitate", "Stato real-time per ogni proprietà", "Tariffe, foto e regolamento personalizzati"],
  },
  {
    id: "prenotazioni", nav: "Prenotazioni",
    title: "Prenotazioni & Calendario", emoji: "📅",
    subtitle: "Vista unificata di tutte le prenotazioni da ogni canale. Import automatico via iCal da Airbnb, Booking.com e Vrbo.",
    Icon: CalendarDays, color: "#5580d6", bg: "#e4eeff",
    features: ["Calendario unificato multi-struttura", "Sync iCal automatica da tutti i canali", "Gestione addebiti, commissioni e tasse"],
  },
  {
    id: "ospiti", nav: "Ospiti & Check-in",
    title: "Database ospiti e check-in", emoji: "👥",
    subtitle: "Ogni ospite viene registrato automaticamente. Il check-in online raccoglie documenti, consenso privacy e firma digitale prima dell'arrivo.",
    Icon: UserCheck, color: "#3aaa85", bg: "#e0f5f0",
    features: ["Anagrafica completa con documenti", "Check-in online con firma digitale", "Contratto locazione generato automaticamente"],
  },
  {
    id: "team", nav: "Team",
    title: "Il tuo team operativo", emoji: "👨‍💼",
    subtitle: "Coordina host, collaboratori, addetti alle pulizie e manutenzione. Assegna strutture e gestisci le attività quotidiane.",
    Icon: Users, color: "#a855f7", bg: "#fdf4ff",
    features: ["Ruoli distinti: Host, Collaboratore, Pulizie", "Attività programmate per struttura", "Ticket manutenzione con priorità urgenza"],
  },
  {
    id: "finanze", nav: "Finanze & Report",
    title: "Finanze e report", emoji: "💰",
    subtitle: "Tieni traccia di entrate, uscite e incasso netto. Tasso di occupazione per struttura e report mensile esportabile.",
    Icon: CircleDollarSign, color: "#22c55e", bg: "#f0fdf4",
    features: ["Ricavi mensili con trend vs mese precedente", "Tasso occupazione per struttura", "Report esportabile per commercialista"],
  },
  {
    id: "messaggi", nav: "Messaggi & Email",
    title: "Automazioni e messaggistica", emoji: "✉️",
    subtitle: "Email automatiche per ogni fase: conferma prenotazione, promemoria check-in, istruzioni accesso e follow-up post-soggiorno.",
    Icon: Mail, color: "#ca8a04", bg: "#fef9c3",
    features: ["4 automazioni pronte all'uso", "Chat diretta con l'ospite", "Personalizzabile per struttura e lingua"],
  },
  {
    id: "guestapp", nav: "Guest App",
    title: "Guest App per ogni ospite", emoji: "📱",
    subtitle: "Un'app mobile personalizzata consegnata via link o QR code. Chatbot AI, segnalazioni, pagamenti e recensioni incluse.",
    Icon: Smartphone, color: "#10b981", bg: "#ecfdf5",
    features: ["Link o QR code per ogni soggiorno", "Chatbot AI con info della struttura", "Segnalazioni, pagamenti e recensioni in app"],
  },
  {
    id: "done", nav: "Inizia",
    title: "Tutto pronto per iniziare!", emoji: "🎉",
    subtitle: "Il tuo account Hedita è attivo. Aggiungi la prima struttura, importa le prenotazioni e invita il tuo team.",
    Icon: CheckCircle2, color: "#22c55e", bg: "#f0fdf4",
    features: ["Aggiungi la tua prima struttura", "Importa prenotazioni via iCal", "Invita i collaboratori del team"],
  },
];

// ── Visual panels (right column) ──────────────────────────────────────────────

function VisualWelcome() {
  const kpis = [
    { label: "Strutture gestite", value: "∞", Icon: Building2, color: "#c87045", bg: "#fdeedd" },
    { label: "Ospiti registrati", value: "500+", Icon: Users, color: "#3aaa85", bg: "#e0f5f0" },
    { label: "Ricavi gestiti", value: "€125K", Icon: CircleDollarSign, color: "#22c55e", bg: "#f0fdf4" },
    { label: "Tasso occupazione", value: "82%", Icon: TrendingUp, color: "#7c3aed", bg: "#f5f3ff" },
  ];
  return (
    <div className="h-full flex flex-col justify-center p-6 gap-4">
      <div className="grid grid-cols-2 gap-3">
        {kpis.map((k, i) => (
          <div key={i} className="bg-card rounded-2xl p-4 border border-[#f0ede9] shadow-sm">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2" style={{ background: k.bg }}>
              <k.Icon className="w-4 h-4" style={{ color: k.color }} />
            </div>
            <div className="text-2xl font-black text-foreground">{k.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{k.label}</div>
          </div>
        ))}
      </div>
      <div className="rounded-2xl p-4 text-white" style={{ background: "#1a1814" }}>
        <p className="text-sm font-semibold mb-2.5">Piattaforma completa per host professionisti</p>
        <div className="flex flex-wrap gap-2">
          {["Multi-struttura", "iCal sync", "Check-in digitale", "Guest App", "Automazioni email", "Report finanziari"].map(t => (
            <span key={t} className="text-xs bg-white/10 text-white/80 px-2.5 py-1 rounded-full">{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function VisualStrutture() {
  const [sel, setSel] = useState(0);
  const items = [
    { name: "Villa Marina", type: "Villa", city: "Ostuni (BR)", camere: 4, stato: "Attiva", emoji: "🏖️" },
    { name: "Masseria Antica", type: "Masseria", city: "Alberobello (BA)", camere: 6, stato: "Attiva", emoji: "🌿" },
    { name: "B&B Trullo", type: "B&B", city: "Fasano (BR)", camere: 3, stato: "In manutenzione", emoji: "🏡" },
  ];
  return (
    <div className="h-full flex flex-col p-5 gap-2.5 justify-center">
      {items.map((s, i) => (
        <div key={i} onClick={() => setSel(i)}
          className={`bg-card rounded-2xl border p-4 cursor-pointer transition-all ${sel === i ? "border-primary shadow-md" : "border-[#f0ede9] hover:shadow-sm"}`}>
          <div className="flex items-center gap-3">
            <span className="text-2xl">{s.emoji}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-foreground text-sm">{s.name}</div>
              <div className="text-xs text-gray-500">{s.type} · {s.city}</div>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.stato === "Attiva" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                {s.stato}
              </span>
              <span className="text-xs text-gray-400">{s.camere} camere</span>
            </div>
          </div>
          {sel === i && (
            <div className="mt-3 pt-3 border-t border-border grid grid-cols-3 gap-2 text-center">
              {["Prenotazioni", "Ospiti", "Manutenzione"].map((l, j) => (
                <div key={j} className="text-xs text-gray-500 bg-background rounded-xl py-1.5">{l}</div>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="bg-gray-50 rounded-2xl border border-dashed border-gray-200 p-3.5 flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-100 transition-colors">
        <span className="text-sm text-gray-400 font-medium">+ Aggiungi struttura</span>
      </div>
    </div>
  );
}

function VisualPrenotazioni() {
  const rows = [
    { ospite: "Sophie M.", struttura: "Villa Marina", periodo: "12–17 Apr", canale: "🏠 Airbnb", stato: "Confermata" },
    { ospite: "Hans W.", struttura: "Masseria Antica", periodo: "10–15 Apr", canale: "📘 Booking", stato: "Confermata" },
    { ospite: "Elena C.", struttura: "B&B Trullo", periodo: "08–12 Apr", canale: "⭐ Diretto", stato: "Check-out" },
    { ospite: "Marco R.", struttura: "Villa Marina", periodo: "18–22 Apr", canale: "🏡 Vrbo", stato: "In arrivo" },
  ];
  return (
    <div className="h-full flex flex-col p-5 gap-3 justify-center">
      <div className="bg-card rounded-2xl border border-[#f0ede9] overflow-hidden shadow-sm">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <span className="text-sm font-semibold text-foreground">Prenotazioni — Aprile 2026</span>
          <span className="text-xs text-[#5580d6] font-medium">Calendario →</span>
        </div>
        <div className="divide-y divide-border">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-2.5">
              <div>
                <div className="text-sm font-medium text-foreground">{r.ospite}</div>
                <div className="text-xs text-gray-500">{r.struttura} · {r.periodo}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r.stato === "Confermata" ? "bg-emerald-50 text-emerald-700" : r.stato === "In arrivo" ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                  {r.stato}
                </span>
                <span className="text-[10px] text-gray-400">{r.canale}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border p-3.5 flex items-center gap-3" style={{ background: "#e4eeff", borderColor: "#c7d7f7" }}>
        <CalendarDays className="w-5 h-5 flex-shrink-0" style={{ color: "#5580d6" }} />
        <div>
          <p className="text-sm font-semibold text-foreground">Sync iCal automatica</p>
          <p className="text-xs text-gray-500">Airbnb · Booking.com · Vrbo aggiornati in tempo reale</p>
        </div>
      </div>
    </div>
  );
}

function VisualOspiti() {
  const [search, setSearch] = useState("");
  const ospiti = [
    { name: "Sophie Müller", country: "🇩🇪 Germania", soggiorni: 3, docs: true },
    { name: "Marco Rossi", country: "🇮🇹 Italia", soggiorni: 1, docs: false },
    { name: "Hans Weber", country: "🇩🇪 Germania", soggiorni: 2, docs: true },
    { name: "Elena Conti", country: "🇮🇹 Italia", soggiorni: 4, docs: true },
  ];
  const filtered = ospiti.filter(o => !search || o.name.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="h-full flex flex-col p-5 gap-3 justify-center">
      <input value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Cerca ospite per nome o email..."
        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#3aaa85] transition-colors" />
      <div className="bg-card rounded-2xl border border-[#f0ede9] overflow-hidden shadow-sm">
        <div className="divide-y divide-border">
          {filtered.map((o, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{ background: "#e0f5f0", color: "#3aaa85" }}>
                {o.name.split(" ").map(p => p[0]).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground">{o.name}</div>
                <div className="text-xs text-gray-500">{o.country} · {o.soggiorni} soggiorn{o.soggiorni === 1 ? "o" : "i"}</div>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${o.docs ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                {o.docs ? "Docs OK" : "Da completare"}
              </span>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-4 py-6 text-sm text-gray-400 text-center">Nessun risultato</div>
          )}
        </div>
      </div>
    </div>
  );
}

function VisualTeam() {
  const members = [
    { name: "Marco Bianchi", role: "Host", strutture: "Villa Marina, B&B Trullo", color: "#c87045", bg: "#fdeedd" },
    { name: "Anna Russo", role: "Pulizie", strutture: "Masseria Antica", color: "#3aaa85", bg: "#e0f5f0" },
    { name: "Luigi Verdi", role: "Manutenzione", strutture: "Tutte le strutture", color: "#5580d6", bg: "#e4eeff" },
  ];
  const attivita = [
    { tipo: "Pulizia", struttura: "Villa Marina", ora: "14:00", stato: "Programmata" },
    { tipo: "Check-in", struttura: "Masseria Antica", ora: "15:30", stato: "In corso" },
    { tipo: "Manutenzione", struttura: "B&B Trullo", ora: "11:00", stato: "Completata" },
  ];
  return (
    <div className="h-full flex flex-col p-5 gap-3 justify-center">
      <div className="space-y-2">
        {members.map((m, i) => (
          <div key={i} className="bg-card rounded-2xl border border-[#f0ede9] p-3.5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: m.bg }}>
              <Users className="w-4 h-4" style={{ color: m.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{m.name}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: m.bg, color: m.color }}>{m.role}</span>
              </div>
              <div className="text-xs text-gray-500 truncate">{m.strutture}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-card rounded-2xl border border-[#f0ede9] overflow-hidden shadow-sm">
        <div className="px-4 py-2.5 border-b border-border">
          <span className="text-xs font-semibold text-foreground">Attività di oggi</span>
        </div>
        {attivita.map((a, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b last:border-0 border-border">
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-sm text-foreground">{a.tipo} · {a.struttura}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">{a.ora}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${a.stato === "In corso" ? "bg-blue-50 text-blue-700" : a.stato === "Completata" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                {a.stato}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VisualFinanze() {
  const kpis = [
    { label: "Ricavi aprile", value: "€ 12.450", trend: "+18%", up: true },
    { label: "Tasso occupazione", value: "78%", trend: "+5%", up: true },
    { label: "Incasso netto", value: "€ 10.820", trend: "+15%", up: true },
  ];
  const bars = [55, 72, 48, 85, 78, 92, 65];
  const days = ["L", "M", "M", "G", "V", "S", "D"];
  return (
    <div className="h-full flex flex-col p-5 gap-3 justify-center">
      <div className="space-y-2.5">
        {kpis.map((k, i) => (
          <div key={i} className="bg-card rounded-2xl border border-[#f0ede9] px-4 py-3 flex items-center justify-between shadow-sm">
            <div>
              <div className="text-xs text-gray-500">{k.label}</div>
              <div className="text-xl font-black text-foreground mt-0.5">{k.value}</div>
            </div>
            <span className="text-sm font-bold px-2.5 py-1 rounded-xl bg-emerald-50 text-emerald-700">{k.trend}</span>
          </div>
        ))}
      </div>
      <div className="bg-card rounded-2xl border border-[#f0ede9] p-4 shadow-sm">
        <div className="text-xs font-semibold text-foreground mb-3">Ricavi ultimi 7 giorni</div>
        <div className="flex items-end gap-1.5 h-14">
          {bars.map((h, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full rounded-t-md transition-all"
                style={{ height: `${h * 0.5}px`, background: i === 5 ? "#c87045" : "#fdeedd" }} />
              <span className="text-[9px] text-gray-400">{days[i]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VisualMessaggi() {
  const [active, setActive] = useState([0, 1, 2, 3]);
  const emails = [
    { Icon: Check, label: "Conferma prenotazione", desc: "Inviata subito alla prenotazione", color: "#22c55e" },
    { Icon: Bell, label: "Promemoria check-in", desc: "3 giorni prima dell'arrivo", color: "#5580d6" },
    { Icon: Home, label: "Istruzioni accesso", desc: "Il giorno del check-in", color: "#c87045" },
    { Icon: Star, label: "Follow-up post-soggiorno", desc: "1 giorno dopo il check-out", color: "#ca8a04" },
  ];
  const toggle = (i: number) => setActive(p => p.includes(i) ? p.filter(x => x !== i) : [...p, i]);
  return (
    <div className="h-full flex flex-col p-5 gap-2.5 justify-center">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Automazioni email — attiva/disattiva</p>
      {emails.map((e, i) => (
        <div key={i} className="bg-card rounded-2xl border border-[#f0ede9] p-3.5 flex items-center gap-3 shadow-sm">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${e.color}18` }}>
            <e.Icon className="w-4 h-4" style={{ color: e.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-foreground">{e.label}</div>
            <div className="text-xs text-gray-400 mt-0.5">{e.desc}</div>
          </div>
          <Toggle value={active.includes(i)} onChange={() => toggle(i)} />
        </div>
      ))}
    </div>
  );
}

function VisualGuestApp() {
  const [tab, setTab] = useState(0);
  const tabs = ["Struttura", "Servizi", "Chat AI", "Segnala"];
  return (
    <div className="h-full flex items-center justify-center p-6 gap-8">
      <div className="w-48 bg-foreground rounded-[2rem] p-1.5 shadow-2xl flex-shrink-0">
        <div className="bg-card rounded-[1.5rem] overflow-hidden" style={{ minHeight: "320px" }}>
          <div className="px-3 pt-4 pb-3" style={{ background: "linear-gradient(135deg, #c87045, #a85030)" }}>
            <div className="text-[10px] text-white/60 mb-0.5">Benvenuto</div>
            <div className="text-sm font-bold text-white">Villa Marina</div>
            <div className="text-[10px] text-white/70 mt-0.5">📍 Ostuni, Puglia</div>
          </div>
          <div className="flex border-b border-gray-100">
            {tabs.map((t, i) => (
              <button key={i} onClick={() => setTab(i)}
                className={`flex-1 py-2 text-[8px] font-semibold transition-colors ${tab === i ? "text-primary border-b-2 border-primary" : "text-gray-400"}`}>
                {t}
              </button>
            ))}
          </div>
          <div className="p-2.5">
            {tab === 0 && (
              <div className="space-y-2">
                <div className="h-16 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg flex items-center justify-center text-[10px] text-gray-400">📷 Foto struttura</div>
                <div className="grid grid-cols-3 gap-1">
                  {["WiFi", "Piscina", "AC", "Cucina", "TV", "Parking"].map(d => (
                    <div key={d} className="bg-gray-50 rounded text-[8px] text-center py-1 text-gray-500">{d}</div>
                  ))}
                </div>
              </div>
            )}
            {tab === 1 && (
              <div className="space-y-1.5">
                {["Colazione 8-10", "Navetta +€20", "Bike rental"].map(s => (
                  <div key={s} className="bg-gray-50 rounded-lg px-2 py-1.5 text-[9px] text-gray-600">{s}</div>
                ))}
              </div>
            )}
            {tab === 2 && (
              <div className="space-y-1.5">
                <div className="bg-primary/10 rounded-lg px-2 py-1.5 text-[9px] text-primary">Ciao! Come posso aiutarti?</div>
                <div className="bg-gray-100 rounded-lg px-2 py-1.5 text-[9px] text-gray-600">A che ora è il check-out?</div>
                <div className="bg-primary/10 rounded-lg px-2 py-1.5 text-[9px] text-primary">Il check-out è entro le 10:00</div>
              </div>
            )}
            {tab === 3 && (
              <div className="space-y-1.5">
                <div className="text-[9px] text-gray-500 mb-1">Segnala un problema</div>
                <div className="bg-gray-50 rounded-lg px-2 py-1.5 text-[9px] text-gray-400">Tipo di problema...</div>
                <div className="bg-gray-50 rounded-lg px-2 py-1.5 text-[9px] text-gray-400 h-8">Descrizione...</div>
                <div className="rounded-lg px-2 py-1.5 text-[9px] text-white text-center" style={{ background: "#c87045" }}>Invia segnalazione</div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {[
          { Icon: Smartphone, label: "Link personalizzato", desc: "Per ogni prenotazione", color: "#10b981", bg: "#ecfdf5" },
          { Icon: MessageSquare, label: "Chatbot AI", desc: "Risponde automaticamente", color: "#5580d6", bg: "#e4eeff" },
          { Icon: AlertTriangle, label: "Segnalazioni", desc: "Dirette dall'ospite", color: "#ca8a04", bg: "#fef9c3" },
          { Icon: Star, label: "Recensioni", desc: "Raccolte in app", color: "#c87045", bg: "#fdeedd" },
        ].map((f, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: f.bg }}>
              <f.Icon className="w-4 h-4" style={{ color: f.color }} />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">{f.label}</div>
              <div className="text-xs text-gray-400">{f.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VisualDone() {
  const [checked, setChecked] = useState([0]);
  const items = [
    "Account Hedita creato ✓",
    "Aggiungi la tua prima struttura",
    "Importa prenotazioni via iCal",
    "Configura le automazioni email",
    "Invita i collaboratori del team",
    "Attiva la Guest App",
  ];
  return (
    <div className="h-full flex flex-col items-center justify-center p-8">
      <div className="text-5xl mb-4">🎉</div>
      <h3 className="text-lg font-bold text-foreground mb-5 text-center">Setup checklist</h3>
      <div className="w-full max-w-sm space-y-2.5">
        {items.map((c, i) => (
          <div key={i} onClick={() => setChecked(p => p.includes(i) ? p.filter(x => x !== i) : [...p, i])}
            className={`flex items-center gap-3 p-3.5 rounded-2xl border cursor-pointer transition-all ${checked.includes(i) ? "bg-emerald-50 border-emerald-200" : "bg-card border-[#f0ede9] hover:border-gray-300"}`}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${checked.includes(i) ? "bg-emerald-500" : "border-2 border-gray-300"}`}>
              {checked.includes(i) && <Check className="w-3 h-3 text-white" />}
            </div>
            <span className={`text-sm font-medium ${checked.includes(i) ? "text-emerald-700" : "text-foreground"}`}>{c}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepVisual({ stepId }: { stepId: string }) {
  switch (stepId) {
    case "welcome":      return <VisualWelcome />;
    case "strutture":    return <VisualStrutture />;
    case "prenotazioni": return <VisualPrenotazioni />;
    case "ospiti":       return <VisualOspiti />;
    case "team":         return <VisualTeam />;
    case "finanze":      return <VisualFinanze />;
    case "messaggi":     return <VisualMessaggi />;
    case "guestapp":     return <VisualGuestApp />;
    case "done":         return <VisualDone />;
    default:             return null;
  }
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function AdminOnboarding() {
  const [current, setCurrent] = useState(0);
  const navigate = useNavigate();

  const step = STEPS[current];
  const progress = ((current + 1) / STEPS.length) * 100;
  const isLast = current === STEPS.length - 1;

  const next = () => { if (!isLast) setCurrent(c => c + 1); else navigate("/"); };
  const back = () => setCurrent(c => Math.max(c - 1, 0));

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#f8f5f0" }}>

      {/* ── Top bar ── */}
      <div className="flex items-center justify-between px-8 py-4 bg-card border-b border-[#f0ede9]">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "#c87045" }}>
            <Building2 className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-foreground text-lg">Hedita</span>
        </div>
        <div className="flex-1 mx-10">
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: step.color }} />
          </div>
          <p className="text-xs text-gray-400 mt-1 text-center">
            {current + 1} di {STEPS.length} — {step.nav}
          </p>
        </div>
        <button onClick={() => navigate("/")} className="text-sm text-gray-400 hover:text-gray-700 transition-colors font-medium">
          Salta il tour →
        </button>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 flex overflow-hidden">

        {/* Left sidebar — step list */}
        <div className="w-56 bg-card border-r border-[#f0ede9] flex flex-col py-6 flex-shrink-0">
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-5 mb-3">Sezioni</p>
          <div className="flex-1 space-y-0.5 px-3">
            {STEPS.map((s, i) => {
              const done = i < current;
              const active = i === current;
              return (
                <button key={i} onClick={() => setCurrent(i)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${active ? "text-white" : done ? "text-gray-600 hover:bg-gray-50" : "text-gray-400 hover:bg-gray-50"}`}
                  style={active ? { background: step.color } : {}}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${active ? "bg-white/25 text-white" : done ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-400"}`}>
                    {done ? <Check className="w-3 h-3" /> : i + 1}
                  </div>
                  <span className="text-sm font-medium">{s.nav}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main content — two columns */}
        <div className="flex-1 flex overflow-hidden">

          {/* Left column — text */}
          <div className="w-[45%] flex flex-col justify-center px-12 py-10 flex-shrink-0">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold mb-5 w-fit"
              style={{ background: step.bg, color: step.color }}>
              <step.Icon className="w-4 h-4" />
              {step.nav}
            </div>
            <h1 className="text-4xl font-black text-foreground leading-tight mb-4">
              {step.title}
            </h1>
            <p className="text-base text-gray-500 leading-relaxed mb-8">{step.subtitle}</p>
            <div className="space-y-3 mb-10">
              {step.features.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: step.bg }}>
                    <Check className="w-3.5 h-3.5" style={{ color: step.color }} />
                  </div>
                  <span className="text-foreground">{f}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              {current > 0 && (
                <button onClick={back}
                  className="flex items-center gap-2 px-5 py-3 rounded-2xl border border-border bg-white text-foreground font-semibold hover:shadow-sm transition-all text-sm">
                  <ChevronLeft className="w-4 h-4" /> Indietro
                </button>
              )}
              <button onClick={next}
                className="flex items-center gap-2 px-8 py-3 rounded-2xl text-white font-semibold hover:opacity-90 transition-all shadow-md text-sm"
                style={{ background: step.color }}>
                {isLast ? "Vai alla Dashboard" : "Avanti"}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-1.5 mt-8">
              {STEPS.map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: i === current ? "24px" : "6px",
                    background: i === current ? step.color : i < current ? "#d1c5bb" : "#e5e0db",
                  }} />
              ))}
            </div>
          </div>

          {/* Right column — interactive visual */}
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="w-full h-full bg-card rounded-3xl border border-[#f0ede9] shadow-sm overflow-hidden">
              <StepVisual stepId={step.id} />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
